import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik } from 'formik';

import { Grid, Button, Box, FormControl, FilledInput, InputLabel, CircularProgress, Select } from '@material-ui/core';

import DialogComponent from 'app/components/DialogComponent';
import CustomTextField from '../../components/TextField';
import DefaultTable from '../../components/DefaultTable';
import lineEndpoint from 'app/api/services/lineEndpoint';
import SaveBox from '../../components/SaveBox';
import Loading from 'app/layouts/Loading';
import { fetchLocations } from 'app/store/slices/line';
import { fetchCities } from 'app/store/slices/forms';
import { STATE_CITY } from 'app/utils/constants';

function ItineraryLocalModal(props) {
    const dispatch = useDispatch();
    const { open, onClose, showNotification } = props;

    const [loading, setLoading] = useState(false);
    const [loadingLocals, setLoadingLocals] = useState(false);
    const [editElement, setEditElement] = useState(null);
    const [pageSize, setPageSize] = useState(5);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [citySearch, setCitySearch] = useState(0);
    const [city, setCity] = useState(0);
    const [allRows, setAllRows] = useState([]);
    const [rows, setRows] = useState([]);
    const [deNome, setDeNome] = useState('');
    const [hasError, setHasError] = useState(false);
    const [locals, setLocals] = useState([]);

    const locationsStore = useSelector((state) => state.line.lineLocations);
    const cities = useSelector((state) => state.forms.cities);

    const handleChangePage = (e, page) => {
        setPage(page);
    };

    const handleChangePageSize = (e) => {
        setPageSize(parseInt(e.target.value));
    };

    const handleChangeSearch = (e) => {
        setSearch(e.target.value);
    };

    const prepareRows = (list) => {
        let rows = pageSize > 0 ? list.slice(page * pageSize, page * pageSize + pageSize) : list;
        rows = rows.map((row) => {
            const temp = { ...row };
            let backgroundColor = 'transparent';
            if (temp.boAlterado) backgroundColor = '#ffebcf';
            if (!temp.cdLinhaLocalidade) backgroundColor = '#b8f5b8';
            if (temp.boExcluido) backgroundColor = '#fbcfca';
            temp.nmMunicipio = temp.municipio?.nmMunicipio || '';
            temp.style = { backgroundColor };
            return temp;
        });
        return rows;
    };

    const handleEdit = (event, data) => {
        if (data.boExcluido) {
            showNotification('Localidade não pode ser editada pois está marcada para deleção.', 'error');
            return;
        }
        setEditElement(data);
        setDeNome(data?.deNome || '');
        setCity(data?.cdMunicipio || 0)
        setHasError(false);
    };

    const findIndexElement = (data) => {
        let index;
        if (data.cdLinhaLocalidade) {
            index = locals.findIndex((element) => element.cdLinhaLocalidade === data.cdLinhaLocalidade);
        } else {
            index = locals.findIndex((element) => element.deNome.toLowerCase() === data.deNome.toLowerCase());
        }
        return index;
    };

    const handleDelete = (event, data) => {
        const index = findIndexElement(data);
        if (index >= 0) {
            const localsChanged = [...locals];
            if (localsChanged[index].boExcluido) {
                delete localsChanged[index].boExcluido;
            } else {
                localsChanged[index] = {
                    ...localsChanged[index],
                    boExcluido: true,
                };
            }
            setLocals(localsChanged);
            setAllRows(localsChanged);
            handleSearch(localsChanged, false);
        }
    };

    const validateForm = () => {
        const errors = [];
        if (deNome === '') {
            errors.push('O nome da localidade é obrigatório. ');
        } else {
            const found = locals.find((element) => {
                let description = element?.deNome || '';
                let cityValue = element?.cdMunicipio || 0;
                return description.toLowerCase() === deNome.toLowerCase() && cityValue === city;
            });
            if (found) {
                if (editElement && editElement.deNome === deNome && editElement.cdMunicipio === city ) {
                    errors.push('Não foi alterado o conteúdo do campo localidade, favor alterar');
                } else {
                    errors.push('Já existe a localidade informada, favor verificar');
                }
            }
        }
        if (!city) {
            errors.push('O município da localidade é obrigatório. ');
        }
        setHasError(errors.length > 0);
        return errors;
    };

    const clear = () => {
        setEditElement(null);
        setHasError(false);
        setDeNome('');
        setCity(0);
        setCitySearch(0);
    };

    const onSaveLocalRow = () => {
        const errors = validateForm();
        if (errors.length > 0) {
            showNotification(errors.join('\n'), 'error');
            return;
        }
        if (editElement != null) {
            const index = findIndexElement(editElement);
            const municipio = cities.find((c) => c.cdMunicipio === Number(city));
            if (index >= 0) {
                const temp = [...locals];
                temp[index] = {
                    ...temp[index],
                    deNome,
                    cdMunicipio: city,
                    municipio: municipio,
                    boAlterado: true,
                };
                setLocals(temp);
                handleSearch(temp, false);
                clear();
            }
            return;
        }
        const list = [...locals];
        const newElement = {
            deNome,
        };
        if (city) {
            const found = cities.find((c) => c.cdMunicipio === Number(city));
            newElement.cdMunicipio = found?.cdMunicipio;
            newElement.municipio = found;
        }
        list.unshift(newElement);
        setLocals(list);
        handleSearch(list, false);
        clear();
    };

    const handleCancel = () => {
        setLocals(locationsStore);
        onClose();
        clear();
        setSearch('');
    };

    const handleSave = async () => {
        setLoading(true);
        const response = await lineEndpoint.saveItineraryLocations({ localidades: locals });
        if (response?.status === 200) {
            setLoadingLocals(true);
            setAllRows(response.data);
            dispatch(fetchLocations());
            setLoadingLocals(false);
            showNotification('Localidades salvas com sucesso', 'success');
            clear();
            setSearch('');
        }
        setLoading(false);
    };

    useEffect(() => {
        (async () => {
            setHasError(false);
            setLoadingLocals(true);
            setAllRows(locationsStore || []);
            if (cities.length === 0) dispatch(fetchCities(STATE_CITY));
            setLoadingLocals(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        (async () => {
            setLocals(locationsStore);
        })();
    }, [locationsStore]);

    useEffect(() => {
        (() => {
            setRows(prepareRows(allRows));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, allRows]);

    const handleSearch = (list, resetPagination) => {
        let slicePage = list;
        if (search !== '')
            slicePage = slicePage.filter((r) => r.deNome.toLowerCase().indexOf(search.toLowerCase()) >= 0);
        if (citySearch) slicePage = slicePage.filter((r) => r.cdMunicipio === Number(citySearch));
        setAllRows(slicePage);
        setRows(prepareRows(slicePage));
        if (page !== 0 && resetPagination) setPage(0);
    };

    return (
        <DialogComponent title={'Localidade'} subtitle={''} open={open} onClose={onClose}>
            <>
                {loading && <Loading />}
                <Formik
                    enableReinitialize
                    initialValues={{}}
                    validationSchema={null}
                    validateOnBlur
                    onSubmit={() => {}}>
                    {() => {
                        return (
                            <Form>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <h4 style={{ marginBottom: 0 }}>Localidade</h4>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Box mb={2}>
                                            <Field
                                                component={CustomTextField}
                                                label="Digite"
                                                name="deNome"
                                                fullWidth
                                                size="small"
                                                margin="dense"
                                                error={hasError}
                                                value={deNome}
                                                onChange={(e) => {
                                                    setHasError(false);
                                                    const name = e.target.value;
                                                    if (name === '') {
                                                        setEditElement(null);
                                                    }
                                                    setDeNome(name.toUpperCase());
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{
                                                    maxLength: 100,
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Box mb={2}>
                                            <FormControl variant="filled" margin="dense" fullWidth error={hasError}>
                                                <InputLabel htmlFor="cdMunicipio" shrink={true}>
                                                    Município
                                                </InputLabel>
                                                <Field
                                                    name="cdMunicipio"
                                                    id="cdMunicipio"
                                                    native
                                                    as={Select}
                                                    value={city}
                                                    onChange={(e) => setCity(e.target.value)}
                                                    validate={(value) => {
                                                        if (!value) return 'Campo obrigatório';
                                                    }}>
                                                    <option aria-label="None" value=""></option>
                                                    {cities.map((c, index) => {
                                                        return (
                                                            <option key={`type-${index}`} value={c.cdMunicipio}>
                                                                {c.nmMunicipio}
                                                            </option>
                                                        );
                                                    })}
                                                </Field>
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Box mt={1}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                disableElevation
                                                size="large"
                                                margin="dense"
                                                fullWidth
                                                style={{ color: '#fff', padding: '11px 22px', borderRadius: '25px' }}
                                                onClick={() => {
                                                    onSaveLocalRow();
                                                }}>
                                                {editElement == null ? 'Adicionar' : 'Alterar'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                        );
                    }}
                </Formik>

                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <h4 style={{ marginBottom: 0 }}>Pesquisar localidade</h4>
                    </Grid>
                    <Grid item xs={5}>
                        <Box mb={2}>
                            <FormControl variant="filled" margin="dense" fullWidth>
                                <InputLabel htmlFor="filled-adornment-password">Informe</InputLabel>
                                <FilledInput
                                    id="buscaLocalidade"
                                    type={'text'}
                                    value={search}
                                    margin="dense"
                                    size="small"
                                    onChange={(e) => handleChangeSearch(e)}
                                    inputProps={{
                                        maxLength: 100,
                                    }}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                    <Grid item xs={5}>
                        <Box mb={2}>
                            <FormControl variant="filled" margin="dense" fullWidth>
                                <InputLabel htmlFor="cdMunicipioSearch" shrink={true}>
                                    Município
                                </InputLabel>
                                <Select
                                    value={citySearch}
                                    onChange={(e) => setCitySearch(e.target.value)}
                                    name="cdMunicipioSearch"
                                    id="cdMunicipioSearch"
                                    native>
                                    <option aria-label="None" value=""></option>
                                    {cities.map((c, index) => {
                                        return (
                                            <option key={`city-${index}`} value={c.cdMunicipio}>
                                                {c.nmMunicipio}
                                            </option>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                    <Grid item xs={2}>
                        <Box mt={1}>
                            <Button
                                variant="contained"
                                color="secondary"
                                disableElevation
                                size="large"
                                fullWidth
                                margin="dense"
                                style={{ color: '#fff', padding: '11px 22px', borderRadius: '25px' }}
                                onClick={() => handleSearch(locals, true)}>
                                Buscar
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        {loadingLocals && (
                            <Grid item xs={12} style={{ textAlign: 'center' }}>
                                <CircularProgress size={16} />
                            </Grid>
                        )}
                        {!loadingLocals && (
                            <DefaultTable
                                columns={[
                                    { id: 0, fieldName: 'cdLinhaLocalidade', title: 'Código' },
                                    { id: 1, fieldName: 'deNome', title: 'Localidade' },
                                    { id: 2, fieldName: 'nmMunicipio', title: 'Município' },
                                    { id: 3, fieldName: '', title: '' },
                                ]}
                                size="small"
                                rows={rows}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                paginationProps={{
                                    rowsPerPageOptions: [5, 10, 25],
                                    count: allRows.length || 0,
                                    rowsPerPage: pageSize,
                                    page: page,
                                    onPageChange: handleChangePage,
                                    onRowsPerPageChange: handleChangePageSize,
                                }}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <SaveBox
                            onCancel={handleCancel}
                            onSave={handleSave}
                            onSaveColor={'secondary'}
                            onSaveStyles={{ color: '#fff' }}
                            removeSpacing={true}
                            firstButtonLabel={'Voltar'}
                        />
                    </Grid>
                </Grid>
            </>
        </DialogComponent>
    );
}

export default ItineraryLocalModal;
